import {FC, FormEvent, ReactNode, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {Link} from 'react-router-dom'
import SharedData from '../../common/SharedData'
import {
  ICatalogPreview,
  ICatalogVariantPreview,
  IFromBarcodePayload,
  IOfferUpdatePayload,
} from '../cors/_models'
import {
  createOfferFromBarcode,
  getOffer,
  lookupBarcode,
  updateOffer,
} from '../cors/_requests'
import {BulkOfferSeed} from '../../products/add-bulk/core/extractBarcodes'
import {toCatalogImageUrl} from '../../../../_metronic/helpers'
import AddVariantsPanel from './AddVariantsPanel'
import VariantMultiOfferForm from './VariantMultiOfferForm'

/**
 * One card in the multi-barcode offer page (B1). Owns the full lifecycle
 * for a single barcode — mirrors the single-barcode tri-state flow
 * (BarcodeField / FromBarcodePage) but resolves INLINE so a list of these
 * can each be created/updated without navigating away:
 *
 *   lookup-barcode →
 *     available      → "not in catalog, add as new product" (no form)
 *     match          → blank form  → createOfferFromBarcode
 *     already_yours  → form pre-filled with the supplier's current offer
 *                      values → updateOffer (PUT /offers/{id})
 *     multiple_match → variant chooser → then create (or update if the
 *                      picked variant is already the supplier's)
 *
 * Cost is never user-entered (server derives it from price × margin); we
 * only show a live read-only preview, identical to MatchOfferForm.
 */

type Outcome = 'created' | 'updated'

type IProps = {
  barcode: string
  onResolved?: (barcode: string, outcome: Outcome) => void
  onRemove?: (barcode: string) => void
  /** True when this list came from a Bulk Add. In that context a barcode the
   *  lookup reports as `available` (not in oc_product) was just submitted as a
   *  product request — so we show "pending review" instead of inviting the
   *  supplier to add it as a new product again. */
  fromBulkAdd?: boolean
  /** Offer values lifted from the Bulk Add sheet row (price/qty/sku), used to
   *  pre-fill the create form so the supplier doesn't re-key what they
   *  already entered in the sheet. */
  seed?: BulkOfferSeed
  /** When the barcode is already the supplier's own product (`already_yours`),
   *  whether to pre-fill the update form with the current offer's price/qty.
   *  Defaults to true (offers page). The add-single-product page passes false
   *  so the supplier types fresh price/qty instead of seeing the live values. */
  prefillOfferValues?: boolean
}

type FormInit = {price: string; quantity: string; supplierSku: string}

type RowState =
  | {kind: 'loading'}
  | {kind: 'error'; message: string}
  | {kind: 'available'}
  | {kind: 'pending_request'}
  | {kind: 'choose'; variants: ICatalogVariantPreview[]}
  | {kind: 'create'; product: ICatalogPreview; productId?: number}
  | {kind: 'update'; product: ICatalogPreview; productSupplierId: number}
  | {kind: 'done'; outcome: Outcome; product: ICatalogPreview; barcode: string}

const toNum = (v: string): number => {
  const n = parseFloat(v)
  return Number.isFinite(n) ? n : NaN
}

/**
 * Card wrapper (barcode badge + remove button + body). MUST live at module
 * scope — defining it inside BarcodeOfferCard would give it a new identity on
 * every render, so React would remount the subtree on each keystroke and the
 * form inputs would lose focus.
 */
const CardShell: FC<{barcode: string; onRemove?: (b: string) => void; children: ReactNode}> = ({
  barcode,
  onRemove,
  children,
}) => {
  const {formatMessage} = useIntl()
  return (
    <div className='bof-shell'>
      {(barcode || onRemove) && (
        <div className='bof-shell-header'>
          {barcode && <span className='bof-barcode-badge'>{barcode}</span>}
          {onRemove && (
            <button
              type='button'
              className='bof-remove-btn'
              onClick={() => onRemove(barcode)}
              title={formatMessage({id: 'offers.from_barcodes.remove'})}
              aria-label={formatMessage({id: 'offers.from_barcodes.remove'})}
            >
              <i className='fas fa-times' aria-hidden='true' />
            </button>
          )}
        </div>
      )}
      {children}
    </div>
  )
}

const BarcodeOfferCard: FC<IProps> = ({barcode, onResolved, onRemove, fromBulkAdd, seed, prefillOfferValues = true}) => {
  const {formatMessage} = useIntl()
  const {currentUser, language} = SharedData()
  const token = currentUser?.token

  const [row, setRow] = useState<RowState>({kind: 'loading'})
  const [price, setPrice] = useState('')
  const [quantity, setQuantity] = useState('')
  const [supplierSku, setSupplierSku] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const apiErr = (e: any): string => {
    const code = e?.response?.data?.error
    return code
      ? formatMessage({id: `offers.form.api_err.${code}`, defaultMessage: code})
      : formatMessage({id: 'something_wrong_desc'})
  }

  const seedForm = (init: FormInit) => {
    setPrice(init.price)
    setQuantity(init.quantity)
    setSupplierSku(init.supplierSku)
    setFormError(null)
  }

  // Fetch the supplier's current offer values to pre-fill the update form.
  // The `already_yours` lookup only carries product_supplier_id, so we read
  // the offer itself (B1 pre-fill default — no backend change needed).
  const enterUpdate = async (product: ICatalogPreview, productSupplierId: number) => {
    try {
      const res = await getOffer(productSupplierId, token, language)
      const o = res.data.data
      // The add-single-product page (prefillOfferValues=false) wants the
      // supplier to type fresh price/qty rather than start from the live offer;
      // the SKU is still carried over. The offers page keeps the full pre-fill.
      seedForm({
        price: prefillOfferValues ? (o.price != null ? String(parseFloat(o.price)) : '') : '',
        quantity: prefillOfferValues ? (o.supplier_quantity != null ? String(o.supplier_quantity) : (o.quantity != null ? String(o.quantity) : '')) : '',
        supplierSku: o.supplier_sku ?? '',
      })
      setRow({kind: 'update', product, productSupplierId})
    } catch (e) {
      setRow({kind: 'error', message: apiErr(e)})
    }
  }

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      setRow({kind: 'loading'})
      try {
        const res = await lookupBarcode(barcode, token, language)
        if (cancelled) return
        const data = res.data
        if (data.state === 'available') {
          setRow({kind: 'available'})
        } else if (data.state === 'pending_request') {
          setRow({kind: 'pending_request'})
        } else if (data.state === 'match') {
          // Pre-fill from the Bulk Add sheet row when present (price/qty/sku);
          // blank otherwise. Only for create — `already_yours` loads the
          // supplier's existing offer values instead.
          seedForm({
            price: seed?.price ?? '',
            quantity: seed?.quantity ?? '',
            supplierSku: seed?.supplierSku ?? '',
          })
          setRow({kind: 'create', product: data.product})
        } else if (data.state === 'already_yours') {
          await enterUpdate(data.product, data.product_supplier_id)
        } else {
          setRow({kind: 'choose', variants: data.products})
        }
      } catch (e) {
        if (!cancelled) setRow({kind: 'error', message: apiErr(e)})
      }
    }
    void run()
    return () => {
      cancelled = true
    }
    // formatMessage stable; intentionally excluded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barcode, token, language])

  const validate = (): string | null => {
    const p = toNum(price)
    const q = parseInt(quantity, 10)
    if (!Number.isFinite(p) || p <= 0) return formatMessage({id: 'offers.form.err.price_invalid'})
    if (!Number.isFinite(q) || q < 0) return formatMessage({id: 'offers.form.err.qty_invalid'})
    if (supplierSku.length > 64) return formatMessage({id: 'offers.form.err.sku_too_long'})
    return null
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (row.kind !== 'create' && row.kind !== 'update') return
    const v = validate()
    if (v) {
      setFormError(v)
      return
    }
    setFormError(null)
    setSubmitting(true)
    const sku = supplierSku.trim() === '' ? null : supplierSku.trim()
    try {
      if (row.kind === 'create') {
        const payload: IFromBarcodePayload = {
          barcode,
          price: toNum(price),
          quantity: parseInt(quantity, 10),
          supplier_sku: sku,
        }
        if (row.productId && row.productId > 0) payload.product_id = row.productId
        await createOfferFromBarcode(payload, token, language)
        setRow({kind: 'done', outcome: 'created', product: row.product, barcode})
        onResolved?.(barcode, 'created')
      } else {
        const payload: IOfferUpdatePayload = {
          price: toNum(price),
          supplier_quantity: parseInt(quantity, 10),
          supplier_sku: sku,
        }
        await updateOffer(row.productSupplierId, payload, token, language)
        setRow({kind: 'done', outcome: 'updated', product: row.product, barcode})
        onResolved?.(barcode, 'updated')
      }
    } catch (e: any) {
      // Race: another tab/device created the offer between lookup and submit.
      const status = e?.response?.status
      const psid = e?.response?.data?.product_supplier_id
      if (row.kind === 'create' && status === 409 && psid) {
        await enterUpdate(row.product, Number(psid))
        setSubmitting(false)
        return
      }
      setFormError(apiErr(e))
    } finally {
      setSubmitting(false)
    }
  }

  // ── presentation ────────────────────────────────────────────────
  if (row.kind === 'loading') {
    return (
      <CardShell barcode={barcode} onRemove={onRemove}>
        <div className="text-muted fs-7">
          <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
          {formatMessage({id: 'offers.from_barcodes.row.loading'})}
        </div>
      </CardShell>
    )
  }

  if (row.kind === 'error') {
    return (
      <CardShell barcode={barcode} onRemove={onRemove}>
        <div className="alert alert-danger mb-0 fs-7" role="alert">{row.message}</div>
      </CardShell>
    )
  }

  if (row.kind === 'available') {
    // From a Bulk Add, an "available" barcode was just submitted as a product
    // request (it isn't in the catalog yet). It needs no action on this page,
    // so hide the card entirely rather than show a note.
    if (fromBulkAdd) {
      return null
    }
    return (
      <CardShell barcode={barcode} onRemove={onRemove}>
        <div className="d-flex flex-column gap-2">
          <div className="fw-bold">{formatMessage({id: 'offers.from_barcodes.row.available_title'})}</div>
          <div className="text-muted fs-7">{formatMessage({id: 'offers.from_barcodes.row.available_body'})}</div>
          <Link
            to={`/products/product-add?barcode=${encodeURIComponent(barcode)}`}
            className="btn btn-sm btn-light-primary align-self-start"
          >
            {formatMessage({id: 'offers.from_barcodes.row.available_cta'})}
          </Link>
        </div>
      </CardShell>
    )
  }

  if (row.kind === 'pending_request') {
    return (
      <CardShell barcode={barcode} onRemove={onRemove}>
        <div className="d-flex align-items-center gap-2">
          <i className="fas fa-clock text-warning" aria-hidden="true" />
          <div>
            <div className="fw-bold">{formatMessage({id: 'offers.from_barcodes.row.pending_title'})}</div>
            <div className="text-muted fs-7">{formatMessage({id: 'offers.from_barcodes.row.pending_body'})}</div>
          </div>
        </div>
      </CardShell>
    )
  }

  if (row.kind === 'choose') {
    // Multi-variant "Sell Yours" — tick one/many/all variants and create an
    // offer for each in one go.
    return (
      <CardShell barcode={barcode} onRemove={onRemove}>
        <VariantMultiOfferForm
          barcode={barcode}
          variants={row.variants}
          onDone={() => onResolved?.(barcode, 'created')}
          prefillOfferValues={prefillOfferValues}
        />
      </CardShell>
    )
  }

  if (row.kind === 'done') {
    return (
      <CardShell barcode={barcode} onRemove={onRemove}>
        <div className="alert alert-success mb-0 d-flex align-items-center justify-content-between fs-7" role="status">
          <span>
            <i className="fas fa-check-circle me-2" aria-hidden="true" />
            {formatMessage({
              id: row.outcome === 'created'
                ? 'offers.from_barcodes.row.created'
                : 'offers.from_barcodes.row.updated',
            })}
            {row.product.name ? ` — ${row.product.name}` : ''}
          </span>
          <Link
            to={`/offers/list?search=${encodeURIComponent(barcode)}`}
            className="btn btn-sm btn-light"
          >
            {formatMessage({id: 'offers.from_barcodes.row.view_offer'})}
          </Link>
        </div>
      </CardShell>
    )
  }

  // create | update — the offer form (Figma states 444 / 333)
  const isUpdate = row.kind === 'update'
  return (
    <CardShell barcode={barcode} onRemove={onRemove}>
      {/* Info banner */}
      <div className='vmof-banner'>
        <i className={`fas ${isUpdate ? 'fa-circle-info' : 'fa-circle-check'}`} aria-hidden='true' />
        <span>
          {isUpdate
            ? formatMessage({id: 'offers.from_barcode.update_banner_single'})
            : formatMessage({id: 'offers.from_barcode.create_banner_single'})}
        </span>
      </div>

      {/* Product header (same as variant form) */}
      <div className='vmof-product'>
        {row.product.image ? (
          <img className='vmof-thumb' src={toCatalogImageUrl(row.product.image)} alt='' loading='lazy' />
        ) : null}
        <div className='vmof-product-info'>
          <div className='vmof-product-name'>{row.product.name ?? '—'}</div>
          {row.product.brand && (
            <div className='vmof-product-meta'>
              {formatMessage({id: 'brand'}) || 'Brand'}: {row.product.brand}
            </div>
          )}
          {!isUpdate && (
            <div className='vmof-product-meta'>
              {formatMessage({id: 'offers.from_barcodes.row.create_header'})}
            </div>
          )}
        </div>
        {isUpdate && (
          <Link to={`/offers/list?search=${encodeURIComponent(barcode)}`} className='vmof-view-live'>
            <i className='fas fa-external-link-alt' aria-hidden='true' />
            {formatMessage({id: 'offers.already_yours.view_live'}) || 'View live offer'}
          </Link>
        )}
      </div>

      {/* Single-product offer table — same structure as variant table, Size/Color hidden */}
      <form onSubmit={submit} aria-busy={submitting}>
        <div className='vmof-table-wrap'>
          <table className='vmof-table'>
            <thead>
              <tr>
                <th>{formatMessage({id: 'offers.form.price'})}</th>
                <th>{formatMessage({id: 'offers.form.quantity'})}</th>
                <th>{formatMessage({id: 'offers.form.supplier_sku'})}</th>
                <th>{formatMessage({id: 'vmof_status'})}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <input
                    type='number'
                    step='0.01'
                    min='0.01'
                    className='vmof-input'
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    disabled={submitting}
                    placeholder='0.00'
                    required
                  />
                </td>
                <td>
                  <input
                    type='number'
                    min='0'
                    className='vmof-input vmof-input--qty'
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    disabled={submitting}
                    placeholder='0'
                    required
                  />
                </td>
                <td>
                  <input
                    type='text'
                    maxLength={64}
                    className='vmof-input vmof-input--sku'
                    value={supplierSku}
                    onChange={(e) => setSupplierSku(e.target.value)}
                    disabled={submitting}
                    placeholder={formatMessage({id: 'optional'})}
                  />
                </td>
                <td>
                  <span className={`vmof-badge ${isUpdate ? 'vmof-badge--active' : 'vmof-badge--none'}`}>
                    {isUpdate
                      ? formatMessage({id: 'vmof_active'})
                      : formatMessage({id: 'vmof_no_offer'})}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {formError && <div className='vmof-error'>{formError}</div>}

        <div className='vmof-actions'>
          <button type='submit' className='vmof-submit' disabled={submitting}>
            {submitting
              ? formatMessage({id: 'offers.from_barcodes.row.submitting'})
              : isUpdate
                ? formatMessage({id: 'offers.from_barcodes.row.update_btn'})
                : formatMessage({id: 'offers.from_barcodes.row.create_btn'})}
          </button>
        </div>
      </form>

      {/* Request a new size/color variant for this product */}
      <AddVariantsPanel barcode={barcode} />
    </CardShell>
  )
}

export default BarcodeOfferCard
